import axios from "axios";
import { panSearchUrl, taskApiPostUrl } from "../config/taskApi";

const CHECK_CUSTOMER_BY_PAN_URL = taskApiPostUrl("checkGetCustomerbyPan");
const CHECK_BLACKLIST_BY_PAN_URL = taskApiPostUrl("checkGetCustomerbyPanBlacklist");

const REAPPLY_ALLOWED_STATUSES = new Set([
  "REJECT",
  "REJECTED",
  "CLOSE",
  "CLOSED",
]);

const FRESH_STATUSES = new Set(["", "FRESH", "NEW"]);

const normalizeLoanStatus = (loanStatus) =>
  String(loanStatus || "").trim().toUpperCase();

const getStatusDecision = (loanStatus) => {
  const cleanStatus = normalizeLoanStatus(loanStatus);

  if (FRESH_STATUSES.has(cleanStatus)) {
    return {
      blocked: false,
      isFresh: true,
      loanStatus: cleanStatus || "FRESH",
      message: "PAN Verification Complete!",
    };
  }

  if (REAPPLY_ALLOWED_STATUSES.has(cleanStatus)) {
    return {
      blocked: false,
      isFresh: false,
      loanStatus: cleanStatus,
      message: `Latest Status: ${cleanStatus} (You can re-apply)`,
    };
  }

  return {
    blocked: true,
    isFresh: false,
    loanStatus: cleanStatus,
    message: `Your loan status is ${cleanStatus}. Cannot re-apply.`,
  };
};

const isJsonObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * Read lead_status from Pansearch.
 * Shape: { status:true, data:[{ database, records:[{ lead_status }] }] }
 */
export const extractPanSearchLoanStatus = (panSearchPayload) => {
  if (!panSearchPayload || panSearchPayload.failed) return { ok: false, status: "" };
  if (!isJsonObject(panSearchPayload)) return { ok: false, status: "" };
  if (panSearchPayload.status === false) return { ok: true, status: "" };

  const list = Array.isArray(panSearchPayload.data) ? panSearchPayload.data : [];
  if (!list.length) return { ok: true, status: "" };

  let fallback = "";
  for (const vertical of list) {
    const records = Array.isArray(vertical?.records) ? vertical.records : [];
    for (const rec of records) {
      const status = normalizeLoanStatus(rec?.lead_status);
      if (!status) continue;
      if (String(vertical.database || "").toLowerCase() === "fundswalla") {
        return { ok: true, status };
      }
      if (!fallback) fallback = status;
    }
  }

  return { ok: true, status: fallback };
};

const fetchPanSearch = async (pancard) => {
  try {
    const resp = await axios.get(panSearchUrl(pancard), {
      timeout: 20000,
      validateStatus: () => true,
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    });
    if (resp.status >= 400 || !isJsonObject(resp.data)) {
      return {
        failed: true,
        status: false,
        message: `Pan search failed (HTTP ${resp.status})`,
      };
    }
    return resp.data;
  } catch (err) {
    return { failed: true, status: false, error: err };
  }
};

/**
 * Blacklist + Pansearch bucket + customer lookup.
 * Pansearch is source of truth when it returns JSON.
 * If Pansearch is down (404/HTML), fall back to checkGetCustomerbyPan.
 * Allowed: Fresh / REJECT / CLOSE(D).
 */
export const checkCustomerByPan = async (pan) => {
  const pancard = String(pan || "").trim().toUpperCase();

  if (pancard.length !== 10) {
    return { blocked: false, isFresh: true };
  }

  try {
    const [customerRes, blacklistRes, panSearchResp] = await Promise.all([
      axios
        .get(CHECK_CUSTOMER_BY_PAN_URL, {
          params: { pancard },
          timeout: 20000,
          validateStatus: () => true,
        })
        .catch((err) => ({ failed: true, error: err })),
      axios
        .get(CHECK_BLACKLIST_BY_PAN_URL, {
          params: { pancard },
          timeout: 20000,
          validateStatus: () => true,
        })
        .catch((err) => ({ failed: true, error: err })),
      fetchPanSearch(pancard),
    ]);

    const blacklistData = blacklistRes?.failed ? null : blacklistRes?.data;
    if (blacklistData?.blacklisted === true) {
      return {
        blocked: true,
        blacklisted: true,
        message:
          blacklistData.message ||
          "This PAN is blacklisted. Cannot apply.",
        customerData: null,
      };
    }

    const customerPayload = customerRes?.failed ? null : customerRes?.data;
    const customerData =
      customerPayload?.status === true && customerPayload?.data
        ? customerPayload.data
        : null;

    const panSearchResult = extractPanSearchLoanStatus(panSearchResp);
    if (panSearchResult.ok) {
      const decision = getStatusDecision(panSearchResult.status);
      return {
        ...decision,
        source: "pansearch",
        customerData,
      };
    }

    const httpStatus = customerRes?.status;
    if (
      !customerPayload ||
      customerPayload.status === false ||
      httpStatus === 404 ||
      !customerPayload.data
    ) {
      return {
        blocked: false,
        isFresh: true,
        loanStatus: "FRESH",
        message: "PAN Verification Complete!",
        source: "customer_fallback",
        panSearchFailed: true,
        customerData: null,
      };
    }

    const decision = getStatusDecision(customerPayload.data.status);
    return {
      ...decision,
      source: "customer_fallback",
      panSearchFailed: true,
      customerData: customerPayload.data,
    };
  } catch (err) {
    return {
      blocked: false,
      isFresh: true,
      checkFailed: true,
      message: "PAN Verification Complete!",
      error: err,
    };
  }
};
